import { ComponentRef, Injectable, ViewContainerRef } from '@angular/core'
import { LoadingComponent } from '../../components/loading/loading.component'

export enum LOADING_ID {
  ASSISTANCES_LOAD = 'Načítám asistence...',
  ASSISTANCE_LOAD = 'Načítám asistenci...',
  FINANCE_LOAD = 'Načítám výkazy...',
  FINANCE_ASSISTANT_LOAD = 'Načítám výkazy asistentů...',
  FINANCE_CLIENT_LOAD = 'Načítám výkazy klientů...',
  FINANCE_EXPORT = 'Exportuji výkazy...',
  FINANCE_ASSISTANT_EXPORT = 'Exportuji výkazy asistentů...',
  FINANCE_CLIENT_EXPORT = 'Exportuji výkazy klientů...',
  FINANCE_SET_BILLED = 'Uzavírám...',
  FINANCE_SET_UNBILLED = 'Otevírám...',
  FINANCE_RECALCULATE = 'Přepočítávám...',
  DEFAULT = 'Načítání...',
}

interface LoaderInstance {
  id: string
  ref: ComponentRef<LoadingComponent>
  timeout?: number
  targetElement?: HTMLElement
  previousPosition?: string
}

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private loaders = new Map<string, LoaderInstance[]>()
  private viewContainerRef: ViewContainerRef | null = null
  private readonly LOADER_TIMEOUT = 5000

  constructor() {}
  /**
   * Sets the ViewContainerRef to allow component creation.
   * @param viewContainerRef - The ViewContainerRef to store.
   */
  setViewContainerRef(viewContainerRef: ViewContainerRef): void {
    this.viewContainerRef = viewContainerRef
  }
  /**
   * Displays a loading indicator.
   * @param message - The message to display from LOADING_ID
   * @param tagName - The tag name to associate with the loader (selector of component).
   * @param targetSelector - Optional CSS selector of a target element to attach the loader. If we don't want to show loader in whole tagName, but only in specific part of the page, use this option.
   * If we have several same tagNames, we can set TagName as 'global' or 'undefined' and add a specific selector. In this case we show loader only in element with this selector.
   * It is advised to use id of the element as selector, it's up to programmer to use unique selector.
   * @param spinnerType - The type of spinner animation (write in kebab-case):
   * Default
   * Dots
   * Lines
   * Lines Small
   * Lines Sharp
   * Lines Sharp Small
   * Bubbles
   * Circles
   * Circular
   * Crescent
   * @param cssClass - The CSS class for styling the loader.
   * @param showBackdrop - Whether to show a backdrop.
   */
  async show(
    message?: LOADING_ID,
    tagName?: string,
    targetSelector?: string,
    spinnerType?: string,
    cssClass?: string,
    showBackdrop?: boolean,
  ): Promise<void> {
    message = message ?? LOADING_ID.DEFAULT
    tagName = tagName ?? 'global'
    spinnerType = spinnerType ?? 'dots'
    cssClass = cssClass ?? 'loadingBanner'
    showBackdrop = showBackdrop ?? true

    if (!this.viewContainerRef) return
    await this.closeExistingLoaders(tagName)

    const targetElement = this.getTargetElement(targetSelector)
    // Couldn't locate HTML element with targetSelector
    if (targetSelector && !targetElement) return

    const componentRef = this.createLoaderComponent(message, spinnerType, cssClass, showBackdrop)
    // Couldn't create loader component
    if (!componentRef) return

    this.attachLoaderToTarget(componentRef, tagName, targetElement)
  }
  /**
   * Hides one or multiple loaders.
   * @param loadersToRemove - Array of loaders to remove, specified by tagName and optional message.
   */
  async hide(loadersToRemove: { tagName: string; message?: string }[] = []): Promise<void> {
    loadersToRemove.forEach(({ tagName, message }) => {
      const loaderArray = this.loaders.get(tagName)
      if (!loaderArray) return

      const remainingLoaders = loaderArray.filter(({ id, ref, timeout, targetElement, previousPosition }) => {
        if (!message || id === message) {
          if (timeout) clearTimeout(timeout)
          ref.destroy()
          this.restoreTargetPosition(targetElement, previousPosition)
          return false
        }
        return true
      })

      this.updateLoaders(tagName, remainingLoaders)
    })
  }
  /**
   * Closes existing loaders for a given tag name.
   * @param tagName - The tag name whose loaders should be closed.
   */
  private async closeExistingLoaders(tagName: string): Promise<void> {
    if (this.loaders.has(tagName)) {
      console.warn(`Closing existing loaders for '${tagName}' before showing a new one.`)
      await this.hide([{ tagName }])
    }
  }
  /**
   * Retrieves the target element by its CSS selector.
   * @param targetSelector - The selector of the target element.
   * @returns The found HTMLElement or null if not found.
   */
  private getTargetElement(targetSelector?: string): HTMLElement | null {
    if (!targetSelector) return null
    const targetElement = document.querySelector(targetSelector) as HTMLElement
    if (!targetElement) {
      console.error(`No element found with selector '${targetSelector}'`)
      return null
    }
    return targetElement
  }
  /**
   * Creates a loading component instance.
   * @param message - The message for the loader from LOADING_ID.
   * @param spinnerType - The spinner type.
   * @param cssClass - The CSS class to apply.
   * @param showBackdrop - Whether to show a backdrop.
   * @returns The created ComponentRef or null.
   */
  private createLoaderComponent(
    message: string,
    spinnerType: string,
    cssClass: string,
    showBackdrop: boolean,
  ): ComponentRef<LoadingComponent> | null {
    if (!this.viewContainerRef) return null
    const componentRef = this.viewContainerRef.createComponent(LoadingComponent)
    componentRef.instance.showBackdrop = showBackdrop
    componentRef.instance.message = message
    componentRef.instance.spinnerType = spinnerType
    componentRef.instance.cssClass = cssClass

    componentRef.changeDetectorRef.detectChanges()
    return componentRef
  }
  /**
   * Attaches the loader component to a specified target.
   * @param componentRef - The component reference.
   * @param tagName - The tag name associated with the loader.
   * @param targetElement - Optional target element to attach to.
   */
  private attachLoaderToTarget(
    componentRef: ComponentRef<LoadingComponent>,
    tagName: string,
    targetElement?: HTMLElement,
  ): void {
    const loaderElement = componentRef.location.nativeElement
    if (!loaderElement) return

    let previousPosition: string | undefined

    if (targetElement) {
      previousPosition = targetElement.style.position
      this.setTargetPosition(targetElement)
      targetElement.appendChild(loaderElement)
    } else {
      const parentElement: HTMLElement =
        tagName !== 'global' ? (document.getElementsByTagName(tagName)[0] as HTMLElement) : document.body
      if (!parentElement) return
      parentElement.appendChild(loaderElement)
    }

    const timeout = window.setTimeout(() => {
      console.warn(
        `Loader for '${tagName}' with message '${componentRef.instance.message}' was automatically hidden after timeout.`,
      )
      this.hide([{ tagName, message: componentRef.instance.message }]).catch((error) =>
        console.error('Error hiding loader:', error),
      )
    }, this.LOADER_TIMEOUT)

    this.loaders.set(tagName, [
      {
        id: componentRef.instance.message,
        ref: componentRef,
        timeout,
        targetElement,
        previousPosition,
      },
    ])
  }

  private setTargetPosition(targetElement: HTMLElement): void {
    if (targetElement.style.position !== 'relative') {
      targetElement.style.position = 'relative'
    }
  }

  private restoreTargetPosition(targetElement?: HTMLElement, previousPosition?: string): void {
    if (targetElement && previousPosition !== undefined) {
      targetElement.style.position = previousPosition
    }
  }

  private updateLoaders(tagName: string, remainingLoaders: LoaderInstance[]): void {
    if (remainingLoaders.length > 0) {
      this.loaders.set(tagName, remainingLoaders)
    } else {
      this.loaders.delete(tagName)
    }
  }
}
